import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Heart, Image as ImageIcon } from 'lucide-react';
import { FaTimes } from 'react-icons/fa';
import SmartImage from './SmartImage';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import { fetchTopReactedMuseumArtworks } from '../utils/museumCoLearningStats';
import {
  persistMostLoved,
  readCachedMostLoved,
  warmMostLovedThumbnails,
} from '../utils/artistSalonCache';
import { coLearningExpandedBackdropProps } from '../ui/coLearningModalTheme';

const TOP_LOVED_COUNT = 3;

function MostLovedExpandedWindow({ item, onClose }) {
  if (!item || typeof document === 'undefined') return null;

  const imageSrc = resolveImageUrlUtil(item.imageUrl) || item.imageUrl;

  return createPortal(
    <div
      role="presentation"
      {...coLearningExpandedBackdropProps}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-[1] mx-4 flex w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={item.title || 'Most loved artwork'}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#e8d4bc]/60 px-4 py-3 sm:px-5">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
              Most loved
            </p>
            <h3 className="truncate text-base font-semibold text-[#2F251B] sm:text-lg">
              {item.title || 'Shared artwork'}
            </h3>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-[#8A7357]">
              <Heart className="h-3.5 w-3.5 fill-rose-400 text-rose-500" aria-hidden />
              {item.totalReactions ?? 0} reaction{item.totalReactions === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/90 text-[#5e5a54] shadow-sm transition hover:bg-white hover:text-[#2f2a24]"
            aria-label="Close"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 items-center justify-center bg-[#f4efe6]/40 p-4 sm:p-6">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={item.title || 'Artwork'}
              className="max-h-[min(72vh,720px)] w-full rounded-2xl object-contain shadow-lg"
            />
          ) : (
            <div className="flex h-48 w-full items-center justify-center rounded-2xl bg-[#f4efe6]">
              <ImageIcon className="h-12 w-12 text-[#e8d4bc]" aria-hidden />
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

function MostLovedThumbnails({ items, loading, onItemClick }) {
  if (loading) {
    return (
      <div className="w-full rounded-2xl border border-[#e8d4bc]/70 bg-gradient-to-br from-[#fffdf9]/90 to-[#fff6ec]/60 px-3 py-2.5 sm:w-auto">
        <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
          Most loved
        </p>
        <div className="flex flex-row justify-center gap-2 sm:justify-start">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 w-14 animate-pulse rounded-xl bg-[#efe2d0]/80 sm:h-16 sm:w-16" />
          ))}
        </div>
      </div>
    );
  }

  if (!items?.length) return null;

  return (
    <div className="w-full rounded-2xl border border-[#e8d4bc]/70 bg-gradient-to-br from-[#fffdf9]/90 to-[#fff6ec]/60 px-3 py-2.5 sm:w-auto">
      <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
        Most loved
      </p>
      <div className="flex flex-row flex-wrap items-center justify-center gap-2 sm:justify-start">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onItemClick(item)}
            className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-[#efe2d0]/90 bg-[#fffdf9] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:h-16 sm:w-16"
            title={item.title}
          >
            {item.imageUrl ? (
              <SmartImage
                src={resolveImageUrlUtil(item.imageUrl) || item.imageUrl}
                alt={item.title}
                className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                containerClassName="h-full w-full"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-[#f4efe6]">
                <ImageIcon className="h-5 w-5 text-[#e8d4bc]" aria-hidden />
              </div>
            )}
            <span className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[9px] font-semibold text-[#2F251B] shadow-sm">
              <Heart className="h-2.5 w-2.5 fill-rose-400 text-rose-500" aria-hidden />
              {item.totalReactions}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Top reacted shared artworks — Instagram-style cache + background refresh. */
export default function ArtistWallMostLoved({ uid }) {
  const [loading, setLoading] = useState(() => {
    const cached = readCachedMostLoved(uid);
    return !(cached && cached.length > 0);
  });
  const [topArtworks, setTopArtworks] = useState(() => readCachedMostLoved(uid) || []);
  const [expandedLoved, setExpandedLoved] = useState(null);
  const revalidateLock = useRef(false);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setTopArtworks([]);
      return;
    }

    let cancelled = false;
    const cached = readCachedMostLoved(uid);

    if (cached?.length) {
      setTopArtworks(cached);
      setLoading(false);
      warmMostLovedThumbnails(cached);
    }

    if (revalidateLock.current) {
      return () => {
        cancelled = true;
      };
    }
    revalidateLock.current = true;

    const revalidate = async () => {
      if (!cached?.length) setLoading(true);
      try {
        const top = await fetchTopReactedMuseumArtworks(uid, TOP_LOVED_COUNT);
        if (!cancelled) {
          persistMostLoved(uid, top);
          setTopArtworks(top);
          warmMostLovedThumbnails(top);
        }
      } catch (err) {
        console.warn('[ArtistWallMostLoved] Failed to load top artworks:', err);
        if (!cancelled && !cached?.length) setTopArtworks([]);
      } finally {
        if (!cancelled) setLoading(false);
        revalidateLock.current = false;
      }
    };

    void revalidate();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!expandedLoved) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setExpandedLoved(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expandedLoved]);

  if (!loading && !topArtworks?.length) {
    return null;
  }

  return (
    <>
      <MostLovedThumbnails
        items={topArtworks}
        loading={loading}
        onItemClick={(item) => setExpandedLoved(item)}
      />
      {expandedLoved ? (
        <MostLovedExpandedWindow item={expandedLoved} onClose={() => setExpandedLoved(null)} />
      ) : null}
    </>
  );
}
